import { Button, Input, Label, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon, XIcon } from "lucide-react";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useMemo, useRef, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditionByFieldAndOp,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { formatDateForInput } from "@/shared/lib/date-time";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { useQuotationInvoiceDocumentTypesQuery } from "../api/queries";

const ISSUE_DATE_FIELD = "issueDate";

/** issue_date ใน DB เป็น timestamp without time zone — ใช้รูปแบบเดียวกับ ContractsFilter */
function issueDateBoundToInputValue(v: unknown): string {
  if (typeof v !== "string" || !v.trim()) return "";
  const s = v.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return formatDateForInput(d);
}

function yyyyMmDdStartToFilterValue(yyyyMmDd: string): string {
  return `${yyyyMmDd.trim()} 00:00:00`;
}

function yyyyMmDdEndToFilterValue(yyyyMmDd: string): string {
  return `${yyyyMmDd.trim()} 23:59:59.999`;
}

export type DocumentTypeTab = "all" | (string & Record<never, never>);
export type DocumentStatusTab =
  | "all"
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "cancelled";

type DocumentsFilterProps = {
  searchParams: OffsetPageQueryDTO & { documentType?: string };
  fromRoute: string;
};

export function DocumentsFilter({ searchParams, fromRoute }: DocumentsFilterProps) {
  const nav = useNavigate({ from: fromRoute as "/app/documents" });
  const search = searchParams;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const searchRef = useRef(search);
  searchRef.current = search;
  const [searchFieldKey, setSearchFieldKey] = useState(0);

  const documentTypeFilter = findCondition(filters, "documentType");
  const documentTypeTab: string = (documentTypeFilter?.value as string) ?? "all";

  const { data: documentTypesData } = useQuotationInvoiceDocumentTypesQuery({
    limit: 100,
  });
  const documentTypeOptions = useMemo(() => {
    const list = documentTypesData?.items ?? [];
    const fromDb = [...list]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((t) => ({ value: t.code, label: t.name }));
    return [{ value: "all", label: "ທັງໝົດ" }, ...fromDb];
  }, [documentTypesData?.items]);
  const statusFilter = findCondition(filters, "status");
  const statusTab: DocumentStatusTab =
    (statusFilter?.value as DocumentStatusTab) ?? "all";
  const searchFilter = findCondition(filters, "search");
  const createdByFilter = findCondition(filters, "createdBy");
  const createdById = (createdByFilter?.value as string) ?? "";
  const searchFromUrl = (searchFilter?.value as string) ?? "";

  const navigateWithFilters = (
    nextFilters: FilterConditionDTO[] | undefined,
  ) => {
    nav({
      search: {
        ...searchRef.current,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const setTab = (tab: string) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (tab === "all") {
      nextFilters = removeConditions(filters, "documentType");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "documentType",
        op: "eq",
        value: tab,
      });
    }
    navigateWithFilters(nextFilters);
  };

  const setStatusTab = (tab: DocumentStatusTab) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (tab === "all") {
      nextFilters = removeConditions(filters, "status");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "status",
        op: "eq",
        value: tab,
      });
    }
    navigateWithFilters(nextFilters);
  };

  const setCreatedBy = (userId: string) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!userId) {
      nextFilters = removeConditions(filters, "createdBy");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "createdBy",
        op: "eq",
        value: userId,
      });
    }
    navigateWithFilters(nextFilters);
  };

  const debouncedSearch = useDebounceCallback((val: string) => {
    const current = filtersRef.current;
    let nextFilters: FilterConditionDTO[] | undefined = current;
    if (val.trim()) {
      nextFilters = upsertCondition(current, {
        field: "search",
        op: "contains",
        value: val.trim(),
      });
    } else {
      nextFilters = removeConditions(current, "search");
    }
    navigateWithFilters(nextFilters);
  }, 400);

  const issueDateGte = filters.find(
    (f) => f.field === ISSUE_DATE_FIELD && f.op === "gte",
  );
  const issueDateLte = filters.find(
    (f) => f.field === ISSUE_DATE_FIELD && f.op === "lte",
  );
  const issueDateFromValue = issueDateBoundToInputValue(issueDateGte?.value);
  const issueDateToValue = issueDateBoundToInputValue(issueDateLte?.value);

  const setIssueDateFrom = (yyyyMmDd: string) => {
    let base =
      removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "gte") ?? [];
    if (yyyyMmDd.trim()) {
      base = [
        ...base,
        {
          field: ISSUE_DATE_FIELD,
          op: "gte",
          value: yyyyMmDdStartToFilterValue(yyyyMmDd.trim()),
        },
      ];
    }
    navigateWithFilters(base);
  };

  const setIssueDateTo = (yyyyMmDd: string) => {
    let base =
      removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "lte") ?? [];
    if (yyyyMmDd.trim()) {
      base = [
        ...base,
        {
          field: ISSUE_DATE_FIELD,
          op: "lte",
          value: yyyyMmDdEndToFilterValue(yyyyMmDd.trim()),
        },
      ];
    }
    navigateWithFilters(base);
  };

  const clearIssueDateRange = () => {
    let base = removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "gte") ?? [];
    base = removeConditionByFieldAndOp(base, ISSUE_DATE_FIELD, "lte") ?? [];
    navigateWithFilters(base);
  };

  const hasFilter =
    !!searchFilter?.value ||
    documentTypeTab !== "all" ||
    statusTab !== "all" ||
    !!createdById ||
    !!issueDateFromValue ||
    !!issueDateToValue;

  const statusTabs: { key: DocumentStatusTab; label: string }[] = [
    { key: "all", label: "ທັງໝົດ" },
    { key: "draft", label: "ຮ່າງ" },
    { key: "issued", label: "ອອກແລ້ວ" },
    { key: "partially_paid", label: "ຊຳລະບາງສ່ວນ" },
    { key: "paid", label: "ຊຳລະແລ້ວ" },
    { key: "cancelled", label: "ຍົກເລີກ" },
  ];

  return (
    <div className="mb-4 flex flex-col gap-4" data-tourid="documents-filter">
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          key={searchFieldKey}
          placeholder="ຄົ້ນຫາເອກະສານ..."
          defaultValue={searchFromUrl}
          onChange={(e) => debouncedSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <Label className="shrink-0 text-muted-foreground text-sm">
              ປະເພດ:
            </Label>
            <SimpleSelect
              value={documentTypeTab}
              onValueChange={setTab}
              placeholder="ທັງໝົດ"
              className="h-9 min-w-[140px]"
              options={documentTypeOptions}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="shrink-0 text-muted-foreground text-sm">
              ສະຖານະ:
            </Label>
            <SimpleSelect
              value={statusTab}
              onValueChange={(val) => setStatusTab(val as DocumentStatusTab)}
              placeholder="ທັງໝົດ"
              className="h-9 min-w-[140px]"
              options={statusTabs.map((tab) => ({
                value: tab.key,
                label: tab.label,
              }))}
            />
          </div>
          <div className="flex min-w-[200px] max-w-[280px] flex-col gap-1">
            <Label className="text-muted-foreground text-xs">ຜູ້ອອກ:</Label>
            <InfiniteCombobox<LookupItem>
              value={createdById}
              onValueChange={setCreatedBy}
              queryKey={["quotation-invoice", "documents-created-by"]}
              queryFn={(args) =>
                fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ທັງໝົດ"
              clearable
              className="h-9"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-40 flex-col gap-1">
              <Label
                htmlFor="qi-filter-issue-from"
                className="text-muted-foreground text-xs"
              >
                ວັນທີອອກ (ຕັ້ງແຕ່)
              </Label>
              <Input
                id="qi-filter-issue-from"
                type="date"
                value={issueDateFromValue}
                onChange={(e) => setIssueDateFrom(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="flex min-w-40 flex-col gap-1">
              <Label
                htmlFor="qi-filter-issue-to"
                className="text-muted-foreground text-xs"
              >
                ຮອດ
              </Label>
              <Input
                id="qi-filter-issue-to"
                type="date"
                value={issueDateToValue}
                onChange={(e) => setIssueDateTo(e.target.value)}
                className="h-9"
              />
            </div>
            {(issueDateFromValue || issueDateToValue) && (
              <button
                type="button"
                onClick={clearIssueDateRange}
                className="mb-0.5 text-muted-foreground text-xs underline-offset-2 hover:text-foreground hover:underline"
              >
                ລ້າງຊ່ວງວັນທີ
              </button>
            )}
          </div>
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchFieldKey((k) => k + 1);
              nav({
                search: {
                  ...searchRef.current,
                  offset: 0,
                  filters: undefined,
                },
              });
            }}
          >
            <XIcon className="mr-1 h-4 w-4" />
            ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
